import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import type { IconName } from "@similarweb/icons";
import type { ExcelDownloadBody } from "@similarweb/pro-logic-components";
import { PaidExcelDialog, useExcelExport } from "@similarweb/pro-logic-components";
import type { IPlainTooltipProps } from "@similarweb/ui-components";
import { IconButton, Popover, PopoverTrigger, Tooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { i18nFilter } from "app/filters/i18nFilter";
import { trackEvent as appcuesTrackEvent } from "app/services/AppcuesService";
import { getPaidExcelProps } from "app/services/PaidExcelService/getPaidExcelProps";
import { allTrackers } from "app/services/track/track";
import { getDaysSinceStoredDate } from "app/UtilitiesAndConstants/UtilityFunctions/getDaysSinceStoredDate";
import { getDateAsText } from "app/UtilitiesAndConstants/UtilityFunctions/timeAndDate";

import { EngagementExcelDownloadPopup } from "./EngagementExcelDownloadPopup";
import { DownloadButtonWrapper, PaidExcelPopoverContent } from "./styledComponents";

const POPOVER_STATE_NAME = "excel.engagement.popup";

interface IPaidExcelDownloadControlProps {
    type: "Excel" | "PNG";
    downloadUrl: string;
    totalCount?: number;
    excelBodyForReq?: object;
    downloadTooltipText?: ReactNode;
    downloadTooltipConfig?: IPlainTooltipProps;
    buttonText?: string;
    iconName: IconName;
    isDisabled?: boolean;
    showExcelEngagementPopup?: boolean;
    trackingFunction?: VoidFunction;
}

/**
 * Renders the paid-excel button + popup + dialog for DownloadButtonMenu (a class component that
 * can't call hooks). Owns the credits-fetch/download lifecycle via pro-logic-components'
 * useExcelExport hook, plus the pro-client-only engagement popup and addon/subscription-upgrade
 * wiring. Translations and tracking for the dialog itself live inside pro-logic-components — see PFG-463.
 */
export const PaidExcelDownloadControl = ({
    type,
    downloadUrl,
    totalCount,
    excelBodyForReq,
    downloadTooltipText,
    downloadTooltipConfig,
    buttonText,
    iconName,
    isDisabled,
    showExcelEngagementPopup,
    trackingFunction,
}: IPaidExcelDownloadControlProps) => {
    const { hasAddonsHub } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const { isTargetForSubscriptionUpgrade, excelChargeReadMoreHref } = useMemo(() => getPaidExcelProps(), []);
    const {
        isExcelLoading,
        isPaidExcelDialogOpen,
        paidExcelData,
        handleDownloadExcel,
        togglePaidExcelDialog,
        closePaidExcelDialog,
    } = useExcelExport({ excelDownloadUrl: downloadUrl, excelBodyForReq: excelBodyForReq as ExcelDownloadBody });

    const [isEngagementOpen, setIsEngagementOpen] = useState<boolean>(
        () => getDaysSinceStoredDate(POPOVER_STATE_NAME) >= 2 && !!showExcelEngagementPopup,
    );

    const closeEngagement = (): void => {
        localStorage.setItem(POPOVER_STATE_NAME, getDateAsText());
        setIsEngagementOpen(false);
    };

    const handleExcelButtonClick = (): void => {
        (trackingFunction ?? (() => allTrackers.trackEvent("Download", "submit-ok", `Table/${type}`)))();
        appcuesTrackEvent(`${type} Button Click`);
        if (isEngagementOpen) {
            closeEngagement();
        }
        void togglePaidExcelDialog();
    };

    const onEngagementCta = (): void => {
        allTrackers.dispatchTrackEvent("pop up", "click", "download excel pop up");
        closeEngagement();
        void togglePaidExcelDialog();
    };

    const onEngagementClose = (): void => {
        allTrackers.dispatchTrackEvent("pop up", "close", "download excel pop up");
        closeEngagement();
    };

    const handleSubscriptionUpgrade = (): void => {
        const urlParams = new URLSearchParams({ compareplans: "true", plansbanner: "credits" });
        window.open(`${swSettings.swsites.login}/subscription-information?${urlParams}`, "_blank");
    };

    const hasUnlimitedExcelRows = swSettings.components?.Home?.resources?.DaaSExcelRowToDCRatioOverwite === 0;
    const isButtonDisabled = hasUnlimitedExcelRows ? isDisabled : !totalCount;

    const triggerButton = (
        <DownloadButtonWrapper
            className="DownloadButtonWrapper"
            data-automation={`Download ${type}`}
        >
            <Tooltip
                content={downloadTooltipText ? downloadTooltipText : `${i18nFilter()("btn.download")} ${type}`}
                enabled={downloadTooltipConfig?.enabled}
            >
                <IconButton
                    key={`single${type}`}
                    type="flat"
                    iconName={iconName}
                    onClick={handleExcelButtonClick}
                    isLoading={isExcelLoading}
                    isDisabled={isButtonDisabled}
                >
                    {buttonText ? i18nFilter()(buttonText) : null}
                </IconButton>
            </Tooltip>
        </DownloadButtonWrapper>
    );

    return (
        <Popover
            open={isPaidExcelDialogOpen || isEngagementOpen}
            placement="bottom-end"
            withArrow={isEngagementOpen}
        >
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            <PaidExcelPopoverContent
                className={isEngagementOpen ? "download-excel-popup-wrapper" : undefined}
                style={isEngagementOpen ? undefined : { padding: 0 }}
            >
                {isEngagementOpen ? (
                    <EngagementExcelDownloadPopup
                        onCloseClick={onEngagementClose}
                        onCtaClick={onEngagementCta}
                    />
                ) : paidExcelData ? (
                    <PaidExcelDialog
                        totalCount={totalCount}
                        paidExcelData={paidExcelData}
                        downloadUrl={downloadUrl}
                        isExcelLoading={isExcelLoading}
                        handleDownloadExcel={handleDownloadExcel}
                        hasAddonHub={hasAddonsHub}
                        onCtaClicked={() => handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.DataCreditsDynamic)}
                        isTargetForSubscriptionUpgrade={isTargetForSubscriptionUpgrade}
                        handleSubscriptionUpgrade={handleSubscriptionUpgrade}
                        excelChargeReadMoreHref={excelChargeReadMoreHref}
                        onDownload={closePaidExcelDialog}
                        onClickOut={togglePaidExcelDialog}
                    />
                ) : null}
            </PaidExcelPopoverContent>
        </Popover>
    );
};
